import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Slider,
  Paper,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Switch,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Assessment,
  TrendingUp,
  Favorite,
  FitnessCenter,
  LocalHospital,
  Timeline,
  Info,
  Save,
  History,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { healthWorthService } from '../services/supabaseService';
import { HealthWorthAssessment } from '../lib/supabase';
import toast from 'react-hot-toast';

interface HealthMetric {
  id: string;
  name: string;
  description: string;
  unit: string;
  inputType: 'number' | 'slider' | 'radio' | 'boolean';
  min?: number;
  max?: number;
  step?: number;
  options?: string[];
  targetValue?: string;
  category: 'cardiovascular' | 'metabolic' | 'strength' | 'body_composition';
}

interface HealthAssessmentData {
  // Cardiovascular metrics
  restingHeartRate: number | null;
  morningHRV: number | null;
  vo2Max: number | null;
  
  // Metabolic metrics
  fastingGlucose: number | null;
  hba1c: number | null;
  ldlHdlRatio: number | null;
  
  // Body composition
  bodyFatPercentage: number | null;
  bodyFatDEXA: boolean;
  waistToHeightRatio: number | null;
  
  // Strength metrics
  squatWeight: number | null;
  deadliftWeight: number | null;
  pullUps: number | null;
  gripStrength: number | null;
  bodyWeight: number | null;
  
  // Additional metrics
  sleepQuality: string;
  stressLevel: string;
  energyLevel: string;
  overallWellbeing: string;
  annualIncomePreTax: number | null;
  age: number | null;
  
  // Timestamp
  assessmentDate: Date;
  notes?: string;
}

interface HealthRecord {
  id: string;
  userId: string;
  data: HealthAssessmentData;
  createdAt: Date;
  futureEarningCapacity: number;
}

const HealthWorth: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mode } = useThemeMode();
  const [currentStep, setCurrentStep] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [formData, setFormData] = useState<HealthAssessmentData>({
    restingHeartRate: null,
    morningHRV: null,
    vo2Max: null,
    fastingGlucose: null,
    hba1c: null,
    ldlHdlRatio: null,
    bodyFatPercentage: null,
    bodyFatDEXA: false,
    waistToHeightRatio: null,
    squatWeight: null,
    deadliftWeight: null,
    pullUps: null,
    gripStrength: null,
    bodyWeight: null,
    sleepQuality: '',
    stressLevel: '',
    energyLevel: '',
    overallWellbeing: '',
    annualIncomePreTax: null,
    age: null,
    assessmentDate: new Date(),
    notes: '',
  });

  // Health assessment history from Supabase
  const [healthHistory, setHealthHistory] = useState<HealthWorthAssessment[]>([]);
  const [loading, setLoading] = useState(true);

  // Prepare chart data
  const chartData = healthHistory
    .slice()
    .reverse() // Reverse to show chronological order
    .map((record, index) => ({
      date: new Date(record.created_at).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: new Date(record.created_at).getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined 
      }),
      futureEarningCapacity: record.future_earning_capacity,
      fullDate: new Date(record.created_at).toLocaleDateString(),
    }));

  // Custom tooltip formatter for the chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      return (
        <Box sx={{
          bgcolor: mode === 'dark' ? 'rgba(15, 15, 35, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #ffd700',
          borderRadius: 1,
          p: 2,
          color: mode === 'dark' ? 'white' : '#1a202c'
        }}>
          <Typography variant="body2" sx={{ color: '#ffd700', mb: 1 }}>
            {payload[0].payload.fullDate}
          </Typography>
          <Typography variant="body1">
            <strong>${value.toLocaleString()}</strong>
          </Typography>
          <Typography variant="caption" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)' }}>
            Future Earning Capacity
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const steps = [
    'Cardiovascular Health',
    'Metabolic Health', 
    'Body Composition',
    'Strength Metrics',
    'Lifestyle Factors',
    'Review & Submit'
  ];

  // Load health assessment history on component mount
  useEffect(() => {
    const loadHealthHistory = async () => {
      if (!user?.id) return;
      
      try {
        setLoading(true);
        const assessments = await healthWorthService.getHealthAssessments(user.id);
        setHealthHistory(assessments);
      } catch (error) {
        console.error('Error loading health assessments:', error);
        toast.error('Failed to load assessment history');
      } finally {
        setLoading(false);
      }
    };

    loadHealthHistory();
  }, [user?.id]);

  // Pre-populate form with user profile data
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        age: user.age || null,
        annualIncomePreTax: user.annual_income || null,
      }));
    }
  }, [user]);

  const handleInputChange = (field: keyof HealthAssessmentData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Calculate Future Earning Capacity based on health metrics and financial factors
  const calculateFutureEarningCapacity = (data: HealthAssessmentData): number => {
    if (!data.annualIncomePreTax || !data.age) {
      return 0; // Cannot calculate without income and age
    }

    // Constants for calculation
    const RETIREMENT_AGE = 65;
    const INFLATION_RATE = 0.03; // 3% annual inflation
    const INVESTMENT_RETURN = 0.07; // 7% annual investment return
    const WORK_YEARS_REMAINING = Math.max(0, RETIREMENT_AGE - data.age);

    if (WORK_YEARS_REMAINING <= 0) {
      return 0; // Already at or past retirement age
    }

    // Calculate health multiplier (0.7 to 1.3 range)
    let healthMultiplier = 1.0;
    let healthPoints = 0;
    let maxHealthPoints = 0;

    // Cardiovascular health impact (30% of health score)
    if (data.restingHeartRate !== null) {
      maxHealthPoints += 10;
      if (data.restingHeartRate < 60) healthPoints += 10;
      else if (data.restingHeartRate < 70) healthPoints += 7;
      else if (data.restingHeartRate < 80) healthPoints += 4;
    }

    if (data.morningHRV !== null) {
      maxHealthPoints += 10;
      if (data.morningHRV > 80) healthPoints += 10;
      else if (data.morningHRV > 60) healthPoints += 7;
      else if (data.morningHRV > 40) healthPoints += 4;
    }

    if (data.vo2Max !== null) {
      maxHealthPoints += 10;
      if (data.vo2Max >= 45) healthPoints += 10;
      else if (data.vo2Max >= 35) healthPoints += 7;
      else if (data.vo2Max >= 25) healthPoints += 4;
    }

    // Metabolic health impact (25% of health score)
    if (data.fastingGlucose !== null) {
      maxHealthPoints += 8;
      if (data.fastingGlucose < 90) healthPoints += 8;
      else if (data.fastingGlucose < 100) healthPoints += 6;
      else if (data.fastingGlucose < 110) healthPoints += 3;
    }

    if (data.hba1c !== null) {
      maxHealthPoints += 8;
      if (data.hba1c < 5.2) healthPoints += 8;
      else if (data.hba1c < 5.7) healthPoints += 6;
      else if (data.hba1c < 6.0) healthPoints += 3;
    }

    if (data.ldlHdlRatio !== null) {
      maxHealthPoints += 7;
      if (data.ldlHdlRatio < 2.0) healthPoints += 7;
      else if (data.ldlHdlRatio < 2.5) healthPoints += 5;
      else if (data.ldlHdlRatio < 3.0) healthPoints += 3;
    }

    // Physical capability impact (25% of health score)
    if (data.bodyFatPercentage !== null) {
      maxHealthPoints += 8;
      if (data.bodyFatPercentage >= 18 && data.bodyFatPercentage <= 20) healthPoints += 8;
      else if (data.bodyFatPercentage >= 15 && data.bodyFatPercentage <= 25) healthPoints += 6;
      else if (data.bodyFatPercentage >= 12 && data.bodyFatPercentage <= 30) healthPoints += 3;
    }

    if (data.waistToHeightRatio !== null) {
      maxHealthPoints += 7;
      if (data.waistToHeightRatio < 0.45) healthPoints += 7;
      else if (data.waistToHeightRatio < 0.50) healthPoints += 5;
      else if (data.waistToHeightRatio < 0.55) healthPoints += 3;
    }

    // Strength and vitality (20% of health score)
    if (data.squatWeight !== null && data.bodyWeight !== null) {
      maxHealthPoints += 4;
      const squatRatio = data.squatWeight / data.bodyWeight;
      if (squatRatio >= 1.25) healthPoints += 4;
      else if (squatRatio >= 1.0) healthPoints += 3;
      else if (squatRatio >= 0.75) healthPoints += 2;
    }

    if (data.deadliftWeight !== null && data.bodyWeight !== null) {
      maxHealthPoints += 4;
      const deadliftRatio = data.deadliftWeight / data.bodyWeight;
      if (deadliftRatio >= 1.5) healthPoints += 4;
      else if (deadliftRatio >= 1.2) healthPoints += 3;
      else if (deadliftRatio >= 1.0) healthPoints += 2;
    }

    if (data.pullUps !== null) {
      maxHealthPoints += 4;
      if (data.pullUps >= 5) healthPoints += 4;
      else if (data.pullUps >= 3) healthPoints += 3;
      else if (data.pullUps >= 1) healthPoints += 2;
    }

    if (data.gripStrength !== null) {
      maxHealthPoints += 4;
      if (data.gripStrength > 35) healthPoints += 4;
      else if (data.gripStrength > 30) healthPoints += 3;
      else if (data.gripStrength > 25) healthPoints += 2;
    }

    // Lifestyle factors impact productivity and longevity
    const lifestyleFactors = [data.sleepQuality, data.stressLevel, data.energyLevel, data.overallWellbeing];
    lifestyleFactors.forEach(factor => {
      if (factor) {
        maxHealthPoints += 2;
        if (factor === 'excellent') healthPoints += 2;
        else if (factor === 'good') healthPoints += 1.5;
        else if (factor === 'fair') healthPoints += 1;
        else healthPoints += 0.5;
      }
    });

    // Calculate health percentage and convert to earning multiplier
    const healthPercentage = maxHealthPoints > 0 ? (healthPoints / maxHealthPoints) : 0.5;
    
    // Health multiplier ranges from 0.7 (poor health) to 1.3 (excellent health)
    // Poor health reduces earning capacity, excellent health increases it
    healthMultiplier = 0.7 + (healthPercentage * 0.6);

    // Calculate future value with compound growth
    let totalFutureValue = 0;
    const adjustedAnnualIncome = data.annualIncomePreTax * healthMultiplier;

    for (let year = 1; year <= WORK_YEARS_REMAINING; year++) {
      // Income grows with inflation
      const inflationAdjustedIncome = adjustedAnnualIncome * Math.pow(1 + INFLATION_RATE, year);
      
      // Calculate present value of this future income (discounted by investment return rate)
      const presentValue = inflationAdjustedIncome / Math.pow(1 + INVESTMENT_RETURN, year);
      
      totalFutureValue += presentValue;
    }

    return Math.round(totalFutureValue);
  };

  const handleNext = () => {
    // Validate current step before proceeding
    const isValid = validateCurrentStep();
    if (!isValid) {
      return; // Don't proceed if validation fails
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setConfirmDialog(true);
    }
  };

  // Validation function for each step
  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 0: // Cardiovascular Health
        if (!formData.restingHeartRate) {
          toast.error('Please enter your resting heart rate');
          return false;
        }
        if (!formData.morningHRV) {
          toast.error('Please enter your morning heart rate variability');
          return false;
        }
        if (!formData.vo2Max) {
          toast.error('Please enter your VO2 Max');
          return false;
        }
        return true;

      case 1: // Metabolic Health
        if (!formData.fastingGlucose) {
          toast.error('Please enter your fasting glucose level');
          return false;
        }
        if (!formData.hba1c) {
          toast.error('Please enter your HbA1c level');
          return false;
        }
        if (!formData.ldlHdlRatio) {
          toast.error('Please enter your LDL/HDL cholesterol ratio');
          return false;
        }
        return true;

      case 2: // Body Composition
        if (!formData.bodyFatPercentage) {
          toast.error('Please enter your body fat percentage');
          return false;
        }
        if (!formData.waistToHeightRatio) {
          toast.error('Please enter your waist-to-height ratio');
          return false;
        }
        // bodyFatDEXA is a boolean, so it's always defined (true or false)
        return true;

      case 3: // Strength Metrics
        if (!formData.bodyWeight) {
          toast.error('Please enter your body weight');
          return false;
        }
        if (!formData.squatWeight) {
          toast.error('Please enter your squat weight');
          return false;
        }
        if (!formData.deadliftWeight) {
          toast.error('Please enter your deadlift weight');
          return false;
        }
        if (!formData.pullUps && formData.pullUps !== 0) {
          toast.error('Please enter your pull-ups count (enter 0 if unable to perform)');
          return false;
        }
        if (!formData.gripStrength) {
          toast.error('Please enter your grip strength');
          return false;
        }
        return true;

      case 4: // Lifestyle Factors
        if (!formData.sleepQuality) {
          toast.error('Please select your sleep quality');
          return false;
        }
        if (!formData.stressLevel) {
          toast.error('Please select your stress level');
          return false;
        }
        if (!formData.energyLevel) {
          toast.error('Please select your energy level');
          return false;
        }
        if (!formData.overallWellbeing) {
          toast.error('Please select your overall wellbeing');
          return false;
        }
        if (!formData.age) {
          toast.error('Please enter your age');
          return false;
        }
        if (!formData.annualIncomePreTax) {
          toast.error('Please enter your annual income');
          return false;
        }
        return true;

      case 5: // Review & Submit
        // Final validation - check all required fields
        return validateAllFields();

      default:
        return true;
    }
  };

  // Comprehensive validation for all required fields
  const validateAllFields = (): boolean => {
    const requiredFields = [
      { field: 'restingHeartRate', name: 'Resting Heart Rate' },
      { field: 'morningHRV', name: 'Morning Heart Rate Variability' },
      { field: 'vo2Max', name: 'VO2 Max' },
      { field: 'fastingGlucose', name: 'Fasting Glucose' },
      { field: 'hba1c', name: 'HbA1c' },
      { field: 'ldlHdlRatio', name: 'LDL/HDL Ratio' },
      { field: 'bodyFatPercentage', name: 'Body Fat Percentage' },
      { field: 'waistToHeightRatio', name: 'Waist-to-Height Ratio' },
      { field: 'bodyWeight', name: 'Body Weight' },
      { field: 'squatWeight', name: 'Squat Weight' },
      { field: 'deadliftWeight', name: 'Deadlift Weight' },
      { field: 'gripStrength', name: 'Grip Strength' },
      { field: 'sleepQuality', name: 'Sleep Quality' },
      { field: 'stressLevel', name: 'Stress Level' },
      { field: 'energyLevel', name: 'Energy Level' },
      { field: 'overallWellbeing', name: 'Overall Wellbeing' },
      { field: 'age', name: 'Age' },
      { field: 'annualIncomePreTax', name: 'Annual Income' },
    ];

    const missingFields = requiredFields.filter(({ field }) => {
      const value = formData[field as keyof HealthAssessmentData];
      return value === null || value === '' || value === undefined;
    });

    // Special case for pullUps - allow 0 as valid
    if (formData.pullUps === null || formData.pullUps === undefined) {
      missingFields.push({ field: 'pullUps', name: 'Pull-ups' });
    }

    if (missingFields.length > 0) {
      toast.error(`Please complete all required fields: ${missingFields.map(f => f.name).join(', ')}`);
      return false;
    }

    return true;
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!user?.id) {
      toast.error('You must be logged in to save assessments');
      return;
    }

    try {
      // Calculate future earning capacity
      const futureEarningCapacity = calculateFutureEarningCapacity(formData);
      
      const assessmentData: Omit<HealthWorthAssessment, 'id' | 'created_at' | 'updated_at'> = {
        user_id: user.id,
        // Cardiovascular metrics (INTEGER fields - round to whole numbers)
        resting_heart_rate: formData.restingHeartRate ? Math.round(formData.restingHeartRate) : null,
        morning_hrv: formData.morningHRV ? Math.round(formData.morningHRV) : null,
        vo2_max: formData.vo2Max, // DECIMAL field - keep decimals
        
        // Metabolic metrics
        fasting_glucose: formData.fastingGlucose ? Math.round(formData.fastingGlucose) : null, // INTEGER field
        hba1c: formData.hba1c, // DECIMAL field - keep decimals
        ldl_hdl_ratio: formData.ldlHdlRatio, // DECIMAL field - keep decimals
        
        // Body composition
        body_fat_percentage: formData.bodyFatPercentage, // DECIMAL field - keep decimals
        body_fat_dexa: formData.bodyFatDEXA,
        waist_to_height_ratio: formData.waistToHeightRatio, // DECIMAL field - keep decimals
        
        // Strength metrics (INTEGER fields - round to whole numbers)
        squat_weight: formData.squatWeight ? Math.round(formData.squatWeight) : null,
        deadlift_weight: formData.deadliftWeight ? Math.round(formData.deadliftWeight) : null,
        pull_ups: formData.pullUps ? Math.round(formData.pullUps) : null,
        grip_strength: formData.gripStrength ? Math.round(formData.gripStrength) : null,
        body_weight: formData.bodyWeight ? Math.round(formData.bodyWeight) : null,
        
        // Lifestyle factors
        sleep_quality: formData.sleepQuality,
        stress_level: formData.stressLevel,
        energy_level: formData.energyLevel,
        overall_wellbeing: formData.overallWellbeing,
        
        // Financial data (INTEGER fields - round to whole numbers)
        annual_income_pre_tax: formData.annualIncomePreTax ? Math.round(formData.annualIncomePreTax) : null,
        age: formData.age ? Math.round(formData.age) : null,
        
        // Calculated values
        future_earning_capacity: futureEarningCapacity,
        
        // Assessment metadata
        assessment_date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
      };

      const newAssessment = await healthWorthService.createHealthAssessment(assessmentData);
      setHealthHistory(prev => [newAssessment, ...prev]);
      
      toast.success(`Health Worth Assessment completed! Future earning capacity: $${futureEarningCapacity.toLocaleString()}`);
      setConfirmDialog(false);
      
      // Reset form for next assessment
      setFormData({
        restingHeartRate: null,
        morningHRV: null,
        vo2Max: null,
        fastingGlucose: null,
        hba1c: null,
        ldlHdlRatio: null,
        bodyFatPercentage: null,
        bodyFatDEXA: false,
        waistToHeightRatio: null,
        squatWeight: null,
        deadliftWeight: null,
        pullUps: null,
        gripStrength: null,
        bodyWeight: null,
        sleepQuality: '',
        stressLevel: '',
        energyLevel: '',
        overallWellbeing: '',
        annualIncomePreTax: null,
        age: null,
        assessmentDate: new Date(),
        notes: '',
      });
      setCurrentStep(0);
    } catch (error) {
      console.error('Error saving health assessment:', error);
      toast.error('Failed to save assessment. Please try again.');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Cardiovascular Health
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" sx={{ color: mode === 'dark' ? '#e74c3c' : '#dc2626', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Favorite /> Cardiovascular Health
            </Typography>
            
            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(231, 76, 60, 0.1)' : 'rgba(220, 38, 38, 0.05)', border: mode === 'dark' ? '1px solid #e74c3c' : '1px solid #dc2626' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                Resting Heart Rate (bpm) *
              </Typography>
              <Typography variant="caption" sx={{ mb: 2, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)', display: 'block' }}>
                Target: &lt; 60 bpm (Optimal health indicator)
              </Typography>
              <TextField
                type="number"
                required
                inputProps={{ step: "1", min: "30", max: "200" }}
                value={formData.restingHeartRate || ''}
                onChange={(e) => handleInputChange('restingHeartRate', e.target.value ? Number(e.target.value) : null)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">bpm</InputAdornment>,
                }}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>

            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(231, 76, 60, 0.1)' : 'rgba(220, 38, 38, 0.05)', border: mode === 'dark' ? '1px solid #e74c3c' : '1px solid #dc2626' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                Morning Heart Rate Variability (HRV) *
              </Typography>
              <Typography variant="caption" sx={{ mb: 2, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)', display: 'block' }}>
                Target: &gt; 80 (Higher values indicate better autonomic function)
              </Typography>
              <TextField
                type="number"
                required
                inputProps={{ step: "1", min: "10", max: "300" }}
                value={formData.morningHRV || ''}
                onChange={(e) => handleInputChange('morningHRV', e.target.value ? Number(e.target.value) : null)}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>

            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(231, 76, 60, 0.1)' : 'rgba(220, 38, 38, 0.05)', border: mode === 'dark' ? '1px solid #e74c3c' : '1px solid #dc2626' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                VO2 Max (ml/kg/min) *
              </Typography>
              <TextField
                type="number"
                required
                inputProps={{ step: "0.1", min: "10", max: "80" }}
                value={formData.vo2Max || ''}
                onChange={(e) => handleInputChange('vo2Max', e.target.value ? Number(e.target.value) : null)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">ml/kg/min</InputAdornment>,
                }}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>
          </Box>
        );

      case 1: // Metabolic Health
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" sx={{ color: mode === 'dark' ? '#f39c12' : '#d97706', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalHospital /> Metabolic Health
            </Typography>
            
            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(243, 156, 18, 0.1)' : 'rgba(217, 119, 6, 0.05)', border: mode === 'dark' ? '1px solid #f39c12' : '1px solid #d97706' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                Fasting Glucose *
              </Typography>
              <Typography variant="caption" sx={{ mb: 2, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)', display: 'block' }}>
                Target: &lt; 90 mg/dL (Optimal metabolic health)
              </Typography>
              <TextField
                type="number"
                required
                inputProps={{ step: "1", min: "50", max: "300" }}
                value={formData.fastingGlucose || ''}
                onChange={(e) => handleInputChange('fastingGlucose', e.target.value ? Number(e.target.value) : null)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">mg/dL</InputAdornment>,
                }}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>

            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(243, 156, 18, 0.1)' : 'rgba(217, 119, 6, 0.05)', border: mode === 'dark' ? '1px solid #f39c12' : '1px solid #d97706' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                HbA1c (Hemoglobin A1c) *
              </Typography>
              <Typography variant="caption" sx={{ mb: 2, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)', display: 'block' }}>
                Target: &lt; 5.2% (Excellent glucose control)
              </Typography>
              <TextField
                type="number"
                required
                inputProps={{ step: "0.1" }}
                value={formData.hba1c || ''}
                onChange={(e) => handleInputChange('hba1c', e.target.value ? Number(e.target.value) : null)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>

            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(243, 156, 18, 0.1)' : 'rgba(217, 119, 6, 0.05)', border: mode === 'dark' ? '1px solid #f39c12' : '1px solid #d97706' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                LDL/HDL Cholesterol Ratio *
              </Typography>
              <Typography variant="caption" sx={{ mb: 2, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)', display: 'block' }}>
                Target: &lt; 2.0 (Optimal lipid profile)
              </Typography>
              <TextField
                type="number"
                required
                inputProps={{ step: "0.1" }}
                value={formData.ldlHdlRatio || ''}
                onChange={(e) => handleInputChange('ldlHdlRatio', e.target.value ? Number(e.target.value) : null)}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>
          </Box>
        );

      case 2: // Body Composition
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" sx={{ color: mode === 'dark' ? '#3498db' : '#2563eb', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assessment /> Body Composition
            </Typography>
            
            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(52, 152, 219, 0.1)' : 'rgba(37, 99, 235, 0.05)', border: mode === 'dark' ? '1px solid #3498db' : '1px solid #2563eb' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                Body Fat Percentage *
              </Typography>
              <Typography variant="caption" sx={{ mb: 2, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)', display: 'block' }}>
                Target: 18-20% (Optimal health range)
              </Typography>
              <TextField
                type="number"
                required
                inputProps={{ step: "0.1" }}
                value={formData.bodyFatPercentage || ''}
                onChange={(e) => handleInputChange('bodyFatPercentage', e.target.value ? Number(e.target.value) : null)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>

            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(52, 152, 219, 0.1)' : 'rgba(37, 99, 235, 0.05)', border: mode === 'dark' ? '1px solid #3498db' : '1px solid #2563eb' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                Waist-to-Height Ratio *
              </Typography>
              <Typography variant="caption" sx={{ mb: 2, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)', display: 'block' }}>
                Target: &lt; 0.45 (Low health risk)
              </Typography>
              <TextField
                type="number"
                required
                inputProps={{ step: "0.01" }}
                value={formData.waistToHeightRatio || ''}
                onChange={(e) => handleInputChange('waistToHeightRatio', e.target.value ? Number(e.target.value) : null)}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>
          </Box>
        );

      case 3: // Strength Metrics
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" sx={{ color: mode === 'dark' ? '#9b59b6' : '#7c3aed', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FitnessCenter /> Strength Metrics
            </Typography>
            
            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(155, 89, 182, 0.1)' : 'rgba(139, 92, 246, 0.05)', border: mode === 'dark' ? '1px solid #9b59b6' : '1px solid #7c3aed' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                Body Weight (for strength ratios) *
              </Typography>
              <TextField
                type="number"
                required
                inputProps={{ step: "1", min: "80", max: "500" }}
                value={formData.bodyWeight || ''}
                onChange={(e) => handleInputChange('bodyWeight', e.target.value ? Number(e.target.value) : null)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">lbs</InputAdornment>,
                }}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>

            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(155, 89, 182, 0.1)' : 'rgba(139, 92, 246, 0.05)', border: mode === 'dark' ? '1px solid #9b59b6' : '1px solid #7c3aed' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                Squat (1RM or working weight) *
              </Typography>
              <Typography variant="caption" sx={{ mb: 2, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)', display: 'block' }}>
                Target: ≥ 1.25× bodyweight
              </Typography>
              <TextField
                type="number"
                required
                inputProps={{ step: "1", min: "0", max: "1000" }}
                value={formData.squatWeight || ''}
                onChange={(e) => handleInputChange('squatWeight', e.target.value ? Number(e.target.value) : null)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">lbs</InputAdornment>,
                }}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>

            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(155, 89, 182, 0.1)' : 'rgba(139, 92, 246, 0.05)', border: mode === 'dark' ? '1px solid #9b59b6' : '1px solid #7c3aed' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                Deadlift (1RM or working weight) *
              </Typography>
              <Typography variant="caption" sx={{ mb: 2, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)', display: 'block' }}>
                Target: ≥ 1.5× bodyweight
              </Typography>
              <TextField
                type="number"
                required
                inputProps={{ step: "1", min: "0", max: "1000" }}
                value={formData.deadliftWeight || ''}
                onChange={(e) => handleInputChange('deadliftWeight', e.target.value ? Number(e.target.value) : null)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">lbs</InputAdornment>,
                }}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>

            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(155, 89, 182, 0.1)' : 'rgba(139, 92, 246, 0.05)', border: mode === 'dark' ? '1px solid #9b59b6' : '1px solid #7c3aed' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                Pull-ups (strict form) *
              </Typography>
              <Typography variant="caption" sx={{ mb: 2, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)', display: 'block' }}>
                Target: ≥ 5 strict pull-ups (enter 0 if unable to perform)
              </Typography>
              <TextField
                type="number"
                required
                inputProps={{ step: "1", min: "0", max: "100" }}
                value={formData.pullUps || ''}
                onChange={(e) => handleInputChange('pullUps', e.target.value ? Number(e.target.value) : null)}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>

            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(155, 89, 182, 0.1)' : 'rgba(139, 92, 246, 0.05)', border: mode === 'dark' ? '1px solid #9b59b6' : '1px solid #7c3aed' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                Grip Strength *
              </Typography>
              <Typography variant="caption" sx={{ mb: 2, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)', display: 'block' }}>
                Target: &gt; 35kg
              </Typography>
              <TextField
                type="number"
                required
                value={formData.gripStrength || ''}
                onChange={(e) => handleInputChange('gripStrength', e.target.value ? Number(e.target.value) : null)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                }}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>
          </Box>
        );

      case 4: // Lifestyle Factors
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUp /> Lifestyle Factors
            </Typography>
            
            <FormControl required>
              <FormLabel sx={{ color: mode === 'dark' ? 'white' : '#1a202c', mb: 1 }}>Sleep Quality *</FormLabel>
              <RadioGroup
                value={formData.sleepQuality}
                onChange={(e) => handleInputChange('sleepQuality', e.target.value)}
                sx={{ '& .MuiFormControlLabel-label': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
              >
                <FormControlLabel value="excellent" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="Excellent (7-9h, deep sleep, wake refreshed)" />
                <FormControlLabel value="good" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="Good (6-8h, mostly restful)" />
                <FormControlLabel value="fair" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="Fair (5-7h, some issues)" />
                <FormControlLabel value="poor" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="Poor (irregular, unrestful)" />
              </RadioGroup>
            </FormControl>

            <FormControl required>
              <FormLabel sx={{ color: mode === 'dark' ? 'white' : '#1a202c', mb: 1 }}>Stress Level *</FormLabel>
              <RadioGroup
                value={formData.stressLevel}
                onChange={(e) => handleInputChange('stressLevel', e.target.value)}
                sx={{ '& .MuiFormControlLabel-label': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
              >
                <FormControlLabel value="excellent" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="Low (well-managed, minimal impact)" />
                <FormControlLabel value="good" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="Moderate (manageable most days)" />
                <FormControlLabel value="fair" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="High (frequent stress, some management)" />
                <FormControlLabel value="poor" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="Very High (overwhelming, poor management)" />
              </RadioGroup>
            </FormControl>

            <FormControl required>
              <FormLabel sx={{ color: mode === 'dark' ? 'white' : '#1a202c', mb: 1 }}>Energy Level *</FormLabel>
              <RadioGroup
                value={formData.energyLevel}
                onChange={(e) => handleInputChange('energyLevel', e.target.value)}
                sx={{ '& .MuiFormControlLabel-label': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
              >
                <FormControlLabel value="excellent" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="High (sustained energy throughout day)" />
                <FormControlLabel value="good" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="Good (occasional afternoon dips)" />
                <FormControlLabel value="fair" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="Fair (frequent fatigue)" />
                <FormControlLabel value="poor" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="Low (chronic fatigue)" />
              </RadioGroup>
            </FormControl>

            <FormControl required>
              <FormLabel sx={{ color: mode === 'dark' ? 'white' : '#1a202c', mb: 1 }}>Overall Wellbeing *</FormLabel>
              <RadioGroup
                value={formData.overallWellbeing}
                onChange={(e) => handleInputChange('overallWellbeing', e.target.value)}
                sx={{ '& .MuiFormControlLabel-label': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
              >
                <FormControlLabel value="excellent" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="Excellent (thriving, optimal health)" />
                <FormControlLabel value="good" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="Good (healthy, minor issues)" />
                <FormControlLabel value="fair" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="Fair (some health concerns)" />
                <FormControlLabel value="poor" control={<Radio sx={{ color: mode === 'dark' ? '#2ecc71' : '#059669' }} />} label="Poor (multiple health issues)" />
              </RadioGroup>
            </FormControl>

            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(46, 204, 113, 0.1)' : 'rgba(34, 197, 94, 0.05)', border: mode === 'dark' ? '1px solid #2ecc71' : '1px solid #059669' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                Age *
              </Typography>
              <Typography variant="caption" sx={{ mb: 2, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)', display: 'block' }}>
                Required: Used to calculate years remaining until retirement (65)
                {user?.age && formData.age === user.age && (
                  <span style={{ color: '#4caf50', fontWeight: 'bold' }}> • Pre-filled from your profile</span>
                )}
              </Typography>
              <TextField
                type="number"
                required
                value={formData.age || ''}
                onChange={(e) => handleInputChange('age', e.target.value ? Number(e.target.value) : null)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">years</InputAdornment>,
                }}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>

            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(46, 204, 113, 0.1)' : 'rgba(34, 197, 94, 0.05)', border: mode === 'dark' ? '1px solid #2ecc71' : '1px solid #059669' }}>
              <Typography variant="body1" sx={{ mb: 2, color: mode === 'dark' ? 'white' : '#1a202c' }}>
                Annual Income (Pre-Tax) *
              </Typography>
              <Typography variant="caption" sx={{ mb: 2, color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)', display: 'block' }}>
                Required: Used to calculate future earning capacity
                {user?.annual_income && formData.annualIncomePreTax === user.annual_income && (
                  <span style={{ color: '#4caf50', fontWeight: 'bold' }}> • Pre-filled from your profile</span>
                )}
              </Typography>
              <TextField
                type="number"
                required
                value={formData.annualIncomePreTax || ''}
                onChange={(e) => handleInputChange('annualIncomePreTax', e.target.value ? Number(e.target.value) : null)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={{ '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' } }}
                fullWidth
              />
            </Card>

            <TextField
              label="Additional Notes (Optional)"
              multiline
              rows={3}
              value={formData.notes || ''}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              sx={{ 
                '& .MuiInputBase-input': { color: mode === 'dark' ? 'white' : '#1a202c' },
                '& .MuiInputLabel-root': { color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)' }
              }}
              fullWidth
            />
          </Box>
        );

      case 5: // Review & Submit
        const currentCapacity = calculateFutureEarningCapacity(formData);
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" sx={{ color: mode === 'dark' ? '#ffd700' : '#d4af37', mb: 2 }}>
              Review Your Health Worth Assessment
            </Typography>
            
            <Card sx={{ p: 3, background: mode === 'dark' ? 'rgba(255, 215, 0, 0.1)' : 'rgba(234, 179, 8, 0.05)', border: mode === 'dark' ? '2px solid #ffd700' : '2px solid rgba(234, 179, 8, 0.3)', textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: '#ffd700', mb: 1 }}>
                ${currentCapacity.toLocaleString()}
              </Typography>
              <Typography variant="h6" sx={{ color: mode === 'dark' ? 'white' : '#1a202c', mb: 2 }}>
                Your Future Earning Capacity
              </Typography>
              <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(26,32,44,0.8)' }}>
                This represents your projected lifetime earning potential based on your health status, age, and current income.
              </Typography>
            </Card>

            <Alert severity="info" sx={{ bgcolor: 'rgba(52, 152, 219, 0.1)', color: mode === 'dark' ? 'white' : '#1a202c' }}>
              <Typography variant="body2">
                📋 <strong>Important:</strong> Once submitted, this assessment cannot be modified. 
                It will be permanently recorded in your health history for tracking progress over time.
              </Typography>
            </Alert>

            <Typography variant="body1" sx={{ color: mode === 'dark' ? 'white' : '#1a202c', textAlign: 'center' }}>
              Assessment Date: {formData.assessmentDate.toLocaleDateString()}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: mode === 'dark' 
        ? 'radial-gradient(ellipse at center, #1a0033 0%, #000000 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)',
      py: 4,
      px: 2
    }}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" sx={{ color: mode === 'dark' ? 'white' : '#1a202c', fontWeight: 'bold', mb: 2 }}>
            Health Worth Assessment
          </Typography>
          <Typography variant="h6" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(26,32,44,0.8)', mb: 4 }}>
            Track your health value over time with evidence-based biomarkers
          </Typography>
          
          {/* Progress */}
          <Box sx={{ mb: 3 }}>
            <LinearProgress 
              variant="determinate" 
              value={(currentStep + 1) / steps.length * 100}
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#ffd700'
                }
              }}
            />
            <Typography variant="body2" sx={{ color: mode === 'dark' ? 'white' : '#1a202c', mt: 1 }}>
              Step {currentStep + 1} of {steps.length}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
            <Button
              onClick={() => setShowHistory(!showHistory)}
              startIcon={<History />}
              variant="outlined"
              sx={{ color: '#ffd700', borderColor: '#ffd700' }}
            >
              {showHistory ? 'Hide' : 'Show'} History
            </Button>
          </Box>
        </Box>

        {!showHistory ? (
          <>
            {/* Stepper */}
            <Stepper activeStep={currentStep} alternativeLabel sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel sx={{ 
                    '& .MuiStepLabel-label': { 
                      color: mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,32,44,0.6)', 
                      fontSize: '0.75rem' 
                    },
                    '& .MuiStepLabel-label.Mui-active': { color: '#ffd700' },
                    '& .MuiStepLabel-label.Mui-completed': { 
                      color: mode === 'dark' ? 'white' : '#1a202c' 
                    }
                  }}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Form Content */}
            <Paper sx={{ 
              p: 4, 
              background: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.9)',
              border: mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: 2,
              mb: 4
            }}>
              {renderStepContent()}
            </Paper>

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                onClick={handleBack}
                disabled={currentStep === 0}
                startIcon={<ArrowBack />}
                sx={{ 
                  color: mode === 'dark' ? 'white' : '#1a202c',
                  borderColor: mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                  '&:hover': { borderColor: mode === 'dark' ? 'white' : '#1a202c' }
                }}
                variant="outlined"
              >
                Back
              </Button>

              <Button
                onClick={handleNext}
                endIcon={currentStep === steps.length - 1 ? <Save /> : <ArrowForward />}
                variant="contained"
                sx={{ 
                  bgcolor: '#ffd700',
                  color: 'black',
                  '&:hover': { bgcolor: '#ffed4e' }
                }}
              >
                {currentStep === steps.length - 1 ? 'Submit Assessment' : 'Next'}
              </Button>
            </Box>
          </>
        ) : (
          /* History View */
          <Paper sx={{ 
            p: 4, 
            background: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.9)',
            border: mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 2
          }}>
            <Typography variant="h5" sx={{ color: mode === 'dark' ? 'white' : '#1a202c', mb: 3 }}>
              Health Worth History
            </Typography>
            
            {healthHistory.length === 0 ? (
              <Typography variant="body1" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,32,44,0.6)', textAlign: 'center', py: 4 }}>
                No assessments recorded yet. Complete your first assessment to start tracking your health worth over time.
              </Typography>
            ) : (
              <>
                {/* Chart Section */}
                {chartData.length > 1 && (
                  <Card sx={{ 
                    p: 3, 
                    background: mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.8)',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: 2,
                    mb: 3
                  }}>
                    <Typography variant="h6" sx={{ color: '#ffd700', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUp /> Future Earning Capacity Trend
                    </Typography>
                    <Box sx={{ height: 300, width: '100%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke={mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} />
                          <XAxis 
                            dataKey="date" 
                            stroke={mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)'}
                            fontSize={12}
                          />
                          <YAxis 
                            stroke={mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)'}
                            fontSize={12}
                            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                          />
                          <RechartsTooltip content={<CustomTooltip />} />
                          <Line 
                            type="monotone" 
                            dataKey="futureEarningCapacity" 
                            stroke="#ffd700" 
                            strokeWidth={3}
                            dot={{ fill: '#ffd700', strokeWidth: 2, r: 6 }}
                            activeDot={{ r: 8, stroke: '#ffd700', strokeWidth: 2, fill: '#fff' }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                    <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,32,44,0.6)', mt: 2, textAlign: 'center' }}>
                      Track how your health improvements translate to increased earning potential over time
                    </Typography>
                  </Card>
                )}

                {/* Table Section */}
              </>
            )}
            
            {healthHistory.length > 0 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: mode === 'dark' ? 'white' : '#1a202c', fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ color: mode === 'dark' ? 'white' : '#1a202c', fontWeight: 'bold' }}>Future Earning Capacity</TableCell>
                      <TableCell sx={{ color: mode === 'dark' ? 'white' : '#1a202c', fontWeight: 'bold' }}>Trend</TableCell>
                      <TableCell sx={{ color: mode === 'dark' ? 'white' : '#1a202c', fontWeight: 'bold' }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {healthHistory.map((record, index) => {
                                        const previousCapacity = index < healthHistory.length - 1 ? healthHistory[index + 1].future_earning_capacity : null;
                  const trend = previousCapacity ? record.future_earning_capacity - previousCapacity : 0;
                      
                      return (
                        <TableRow key={record.id}>
                          <TableCell sx={{ color: mode === 'dark' ? 'white' : '#1a202c' }}>
                            {new Date(record.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell sx={{ color: mode === 'dark' ? 'white' : '#1a202c' }}>
                            <Chip 
                              label={`$${record.future_earning_capacity.toLocaleString()}`}
                              color={record.future_earning_capacity >= 1000000 ? 'success' : record.future_earning_capacity >= 500000 ? 'warning' : 'error'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ color: mode === 'dark' ? 'white' : '#1a202c' }}>
                            {trend > 0 && (
                              <Chip label={`+$${trend.toLocaleString()}`} color="success" size="small" />
                            )}
                            {trend < 0 && (
                              <Chip label={`-$${Math.abs(trend).toLocaleString()}`} color="error" size="small" />
                            )}
                            {trend === 0 && index > 0 && (
                              <Chip label="No change" color="default" size="small" />
                            )}
                            {index === healthHistory.length - 1 && (
                              <Chip label="First assessment" color="info" size="small" />
                            )}
                          </TableCell>
                          <TableCell sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,32,44,0.7)' }}>
                            {record.notes || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog}
          onClose={() => setConfirmDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: mode === 'dark' 
                ? 'linear-gradient(135deg, #1a0033 0%, #2d1b69 100%)'
                : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              border: '2px solid #ffd700',
              color: mode === 'dark' ? 'white' : '#1a202c',
            }
          }}
        >
          <DialogTitle>
            <Typography variant="h6">Confirm Health Worth Assessment</Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you ready to submit your Health Worth Assessment?
            </Typography>
            <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(26,32,44,0.8)' }}>
              Your future earning capacity: <strong>${calculateFutureEarningCapacity(formData).toLocaleString()}</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,32,44,0.6)', mt: 2 }}>
              Note: This record will be permanent and cannot be modified after submission.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog(false)} sx={{ color: mode === 'dark' ? 'white' : '#1a202c' }}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#ffd700', color: 'black' }}>
              Submit Assessment
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default HealthWorth; 